import * as actionTypes from '../constantes/actionTypes';
import { AnalyseBarContainer, AnalyseContainer, AnalyseMainText, AnalyzeCanvas, BarContainer, Curtain, EmptyBar, FillingBar } from '../styles/analyse';
import { CliniqueState, StepView } from '../type';

import { Predictions } from '@imaaagery/a3camera';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { buildAnalyseStaticMaskEdges, getPredictionMeshPoints, MeshEdge, MeshPoint, normalizeMeshPoint } from '../constantes/loadingMaskMesh';
import { sendAnalyticsPageView } from '../constantes/utilitaries';
import i18n from '../i18nextInit';
import { trackPlausibleEvent } from '../services/analytics';
//import photoTest from '../assets/Images/photo.png'

type AnalyseProps = {
	isComplete?: boolean;
	minimumDurationMs?: number;
	nextStep?: StepView;
	startWithoutPredictions?: boolean;
	staticText?: string;
	completedEventName?: string;
};

const Analyse = ({ isComplete, minimumDurationMs = 18000, nextStep = StepView.ready, startWithoutPredictions = false, staticText, completedEventName = 'sd_loading_completed' }: AnalyseProps) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const [text, setText] = useState<string>(staticText ?? t('skinalysis-in-progress'));
	const analyses = useSelector((state: CliniqueState) => state.session.analyses);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const imageUser = useSelector((state: CliniqueState) => state.session.imageUser);
	const [isResultsEnable, enableResults] = useState<boolean>(false);
	const market = useSelector((state: CliniqueState) => state.session.market);
	const sessionID = useSelector((state: CliniqueState) => state.session.sessionID);
	const predictions = useSelector((state: CliniqueState) => state.session.predictions);
	const intervalRef = useRef<number | null>(null);
	const timersRef = useRef<number[]>([]);
	const animationFrameRef = useRef<number | null>(null);

	const mainTextHandler = () => {
		timersRef.current.forEach(window.clearTimeout);
		timersRef.current = [];

		timersRef.current.push(window.setTimeout(() => {
			enableResults(true);
		}, minimumDurationMs));

		if (staticText) {
			setText(staticText);
			return;
		}

		timersRef.current.push(window.setTimeout(() => {
			setText(t('plot-80-plus-data-points'));
		}, 4000));

		timersRef.current.push(window.setTimeout(() => {
			setText(t('built-50-plus-derm-expertise'));
		}, 8000));

		timersRef.current.push(window.setTimeout(() => {
			setText(t('and-2-plus-million-scans'));
		}, 12000));

		timersRef.current.push(window.setTimeout(() => {
			setText(t('skinalysis-in-progress'));
		}, 16000));
	};

	useEffect(() => {
		if ((isComplete ?? analyses.length > 0) && isResultsEnable) {
			trackPlausibleEvent(completedEventName);
			dispatch({ type: actionTypes.SWITCH_STEP, value: nextStep });
		}
	}, [analyses, completedEventName, dispatch, isComplete, isResultsEnable, nextStep]);

	useEffect(() => {
		if (!imageUser && startWithoutPredictions) {
			mainTextHandler();
			sendAnalyticsPageView(2, market, sessionID);
			return;
		}

		const image = new Image();
		image.src = imageUser;

		image.onload = () => {
			const canvas = canvasRef.current;
			if (!canvas) return;
			const ctx = canvas.getContext('2d');
			if (!ctx) return;

			canvas.width = image.width;
			canvas.height = image.height;
			ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

			if (predictions) {
				startMeshWaveAnimation(predictions, ctx, image);
			}

			if (predictions || startWithoutPredictions) {
				mainTextHandler();
				if (!staticText) {
					intervalRef.current = window.setInterval(mainTextHandler, 20000);
				}

				sendAnalyticsPageView(2, market, sessionID);
			}
		};

		//const predictions = require("../assets/Data/Prediction.json")

		return () => {
			image.onload = null;
			if (intervalRef.current !== null) {
				window.clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
			if (animationFrameRef.current !== null) {
				window.cancelAnimationFrame(animationFrameRef.current);
				animationFrameRef.current = null;
			}
			timersRef.current.forEach(window.clearTimeout);
			timersRef.current = [];
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [predictions, imageUser, startWithoutPredictions, staticText]);

	const getAnnotationMesh = (predictions: Predictions, width: number, height: number) => {
		const points: MeshPoint[] = [];
		const edges: MeshEdge[] = [];
		const annotations = predictions.annotations;

		Object.values(annotations).forEach((contour) => {
			if (!Array.isArray(contour) || contour.length < 2) return;
			const firstIndex = points.length;
			contour.forEach((point) => points.push(normalizeMeshPoint(point, width, height)));
			for (let index = 1; index < contour.length; index++) {
				edges.push([firstIndex + index - 1, firstIndex + index]);
			}
		});

		return { points, edges };
	};

	const smootherStep = (value: number) => {
		const t = Math.max(0, Math.min(1, value));
		return t * t * t * (t * (t * 6 - 15) + 10);
	};

	const clamp01 = (value: number) => Math.max(0, Math.min(1, value));

	const buildAdjacency = (edges: MeshEdge[]) => {
		const adjacency = new Map<number, Set<number>>();
		edges.forEach(([start, end]) => {
			adjacency.set(start, (adjacency.get(start) ?? new Set()).add(end));
			adjacency.set(end, (adjacency.get(end) ?? new Set()).add(start));
		});
		return adjacency;
	};

	const computeWaveDistances = (edges: MeshEdge[], seed?: number) => {
		const adjacency = buildAdjacency(edges);
		const allPointIds = Array.from(adjacency.keys());
		if (allPointIds.length === 0) {
			return { distances: new Map<number, number>(), maxLevel: 0 };
		}

		const origin = seed ?? allPointIds[Math.floor(Math.random() * allPointIds.length)];
		const distances = new Map<number, number>([[origin, 0]]);
		const queue = [origin];

		for (let head = 0; head < queue.length; head++) {
			const current = queue[head];
			const distance = distances.get(current) ?? 0;
			(adjacency.get(current) ?? new Set()).forEach((neighbor) => {
				if (!distances.has(neighbor)) {
					distances.set(neighbor, distance + 1);
					queue.push(neighbor);
				}
			});
		}

		return { distances, maxLevel: Math.max(...Array.from(distances.values())) };
	};

	const pointWaveIntensity = (distance: number | undefined, maxLevel: number, progress: number) => {
		if (distance === undefined) return 0;

		const maxDistance = Math.max(maxLevel, 1);
		const growEnd = 0.74;
		const tailStart = 0.16;
		const cycleFadeInEnd = 0.08;
		const cycleFadeOutStart = 0.9;
		const cycleEnvelope = smootherStep(progress / cycleFadeInEnd) * (1 - smootherStep((progress - cycleFadeOutStart) / (1 - cycleFadeOutStart)));
		const leadingFront = smootherStep(progress / growEnd) * (maxDistance + 2.4);
		const trailingFront = progress > tailStart ? smootherStep((progress - tailStart) / (1 - tailStart)) * (maxDistance + 2.4) : -1;
		const leadingFade = smootherStep(clamp01((leadingFront - distance) / 1.85));
		const trailingFade = smootherStep(clamp01((trailingFront - distance) / 2.35));

		return clamp01(leadingFade * (1 - trailingFade) * cycleEnvelope);
	};

	const drawModernMeshWave = (ctx: CanvasRenderingContext2D, points: MeshPoint[], edges: MeshEdge[], distances: Map<number, number>, maxLevel: number, progress: number, timestamp: number) => {
		const activePointIds = new Set<number>();
		edges.forEach(([start, end]) => {
			activePointIds.add(start);
			activePointIds.add(end);
		});

		edges.forEach(([start, end]) => {
			const startPoint = points[start];
			const endPoint = points[end];
			if (!startPoint || !endPoint) return;

			const startIntensity = pointWaveIntensity(distances.get(start), maxLevel, progress);
			const endIntensity = pointWaveIntensity(distances.get(end), maxLevel, progress);
			const intensity = Math.min(startIntensity, endIntensity);
			if (intensity <= 0.03) return;

			ctx.beginPath();
			ctx.moveTo(startPoint.x, startPoint.y);
			ctx.lineTo(endPoint.x, endPoint.y);
			ctx.strokeStyle = `rgba(255,255,255,${0.18 + intensity * 0.62})`;
			ctx.lineWidth = 0.7 + intensity * 1.15;
			ctx.stroke();

			if (intensity > 0.72) {
				ctx.beginPath();
				ctx.moveTo(startPoint.x, startPoint.y);
				ctx.lineTo(endPoint.x, endPoint.y);
				ctx.strokeStyle = `rgba(255,255,255,${(intensity - 0.72) * 0.32})`;
				ctx.lineWidth = 3.7;
				ctx.stroke();
			}
		});

		activePointIds.forEach((pointId) => {
			const point = points[pointId];
			if (!point) return;

			const wave = pointWaveIntensity(distances.get(pointId), maxLevel, progress);
			const slowTwinkle = (Math.sin(timestamp * 0.0032 + pointId * 1.73) + 1) / 2;
			const fastTwinkle = (Math.sin(timestamp * 0.014 + pointId * 2.41) + 1) / 2;
			const baseOpacity = 0.32 + slowTwinkle * 0.18;
			const waveOpacity = wave * (0.5 + fastTwinkle * 0.34);
			const radius = 1.75 + slowTwinkle * 0.65 + wave * (2.7 + fastTwinkle * 1.05);

			if (wave > 0.35) {
				ctx.beginPath();
				ctx.fillStyle = `rgba(255,255,255,${wave * (0.08 + fastTwinkle * 0.08)})`;
				ctx.arc(point.x, point.y, radius + 3.8 * wave, 0, 2 * Math.PI);
				ctx.fill();
			}

			ctx.beginPath();
			ctx.fillStyle = `rgba(255,255,255,${Math.min(0.9, baseOpacity + waveOpacity)})`;
			ctx.arc(point.x, point.y, radius, 0, 2 * Math.PI);
			ctx.fill();
		});
	};

	const startMeshWaveAnimation = (predictions: Predictions, ctx: CanvasRenderingContext2D, image: HTMLImageElement) => {
		if (animationFrameRef.current !== null) {
			window.cancelAnimationFrame(animationFrameRef.current);
			animationFrameRef.current = null;
		}

		const width = ctx.canvas.width;
		const height = ctx.canvas.height;
		const predictionPoints = getPredictionMeshPoints(predictions, width, height);
		const annotationMesh = getAnnotationMesh(predictions, width, height);
		const points = predictionPoints.length >= 468 ? predictionPoints : annotationMesh.points;
		const edges = predictionPoints.length >= 468
			? buildAnalyseStaticMaskEdges(points)
			: annotationMesh.edges;

		if (points.length === 0 || edges.length === 0) {
			ctx.drawImage(image, 0, 0, width, height);
			return;
		}

		const cycleDuration = 3000;
		let currentCycle = -1;
		let wave = computeWaveDistances(edges);
		let startTimestamp: number | null = null;

		const render = (timestamp: number) => {
			if (startTimestamp === null) {
				startTimestamp = timestamp;
			}

			const elapsed = timestamp - startTimestamp;
			const cycle = Math.floor(elapsed / cycleDuration);
			if (cycle !== currentCycle) {
				wave = computeWaveDistances(edges);
				currentCycle = cycle;
			}

			ctx.clearRect(0, 0, width, height);
			ctx.drawImage(image, 0, 0, width, height);
			drawModernMeshWave(ctx, points, edges, wave.distances, wave.maxLevel, (elapsed % cycleDuration) / cycleDuration, timestamp);
			animationFrameRef.current = window.requestAnimationFrame(render);
		};

		animationFrameRef.current = window.requestAnimationFrame(render);
	};

	return (
		<AnalyseContainer initial={{ x: window.RTL_LANGS.includes(i18n.language) || i18n.language === 'ArAr' ? '-100%' : '100%' }} animate={{ x: 0 }} exit={{ x: window.RTL_LANGS.includes(i18n.language) || i18n.language === 'ArAr' ? '100%' : '-100%' }} transition={{ type: 'spring', duration: 0.5 }}>
			<AnalyzeCanvas ref={canvasRef} />
			<Curtain />
			<AnalyseBarContainer>
				<AnalyseMainText animate={{ opacity: [0, 1, 1, 0] }} transition={{ repeat: 12, duration: 4, repeatDelay: 0, times: [0, 0.2, 0.8, 1] }}>
					{text}
				</AnalyseMainText>

				<BarContainer>
					<EmptyBar>
						<FillingBar animate={{ width: '100%', transition: { duration: 4, delay: 0, ease: 'linear' } }} />
					</EmptyBar>

					<EmptyBar>
						<FillingBar animate={{ width: '100%', transition: { duration: 4, delay: 4, ease: 'linear' } }} />
					</EmptyBar>

					<EmptyBar>
						<FillingBar animate={{ width: '100%', transition: { duration: 4, delay: 8, ease: 'linear' } }} />
					</EmptyBar>

					<EmptyBar>
						<FillingBar animate={{ width: '100%', transition: { duration: 4, delay: 12, ease: 'linear' } }} />
					</EmptyBar>

					{/* <EmptyBar>
						<FillingBar animate={{ width: '100%', transition: { duration: 4, delay: 16, ease: 'linear' } }} />
					</EmptyBar> */}
				</BarContainer>
			</AnalyseBarContainer>
		</AnalyseContainer>
	);
};

const AnalyseComponent = (props: AnalyseProps) => {
	return <Analyse {...props} />;
};

export default AnalyseComponent;
